"use client";
/**
 * Kontakt / Contact — Svendetid marketing contact page content.
 * Language-parametrized so /contact (da) and /en/contact (en) share one source.
 * Uses the shared svendetid-ds design system (same nav + footer as
 * /features and /pricing).
 */
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import {
  dsFontClass, dsTokensCss, Icon, Footer, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

const SUPPORT_EMAIL = "kontakt@svendetid.dk";

const T = {
  da: {
    eyebrow: "Kontakt",
    h1: "Vi er kun en besked væk.",
    sub: "Har du spørgsmål om Svendetid, priser eller hvordan du kommer i gang? Skriv til os — vi svarer på dansk.",
    cards: [
      { icon: "paper-plane-outline", title: "E-mail", body: "Skriv til vores supportteam, så vender vi tilbage hurtigst muligt.", action: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}` },
      { icon: "chatbubbles-outline", title: "Salg & spørgsmål", body: "Vil du høre mere om Business+ og SvendAI? Tag fat i os.", action: "Skriv til salg", href: `mailto:${SUPPORT_EMAIL}?subject=Salg` },
      { icon: "phone-portrait-outline", title: "Status & support", body: "Tjek driftsstatus, hvis noget ikke virker som forventet.", action: "Se status", href: "/status" },
    ],
    bandH2: "Klar til at prøve det selv?",
    bandBody: "Kom i gang gratis i dag — ingen binding.",
    tryFree: "Prøv det gratis",
    seePricing: "Se priser",
  },
  en: {
    eyebrow: "Contact",
    h1: "We're just a message away.",
    sub: "Got questions about Svendetid, pricing or how to get started? Drop us a line — we'll get back to you.",
    cards: [
      { icon: "paper-plane-outline", title: "Email", body: "Write to our support team and we'll get back to you as soon as possible.", action: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}` },
      { icon: "chatbubbles-outline", title: "Sales & questions", body: "Want to hear more about Business+ and SvendAI? Get in touch.", action: "Email sales", href: `mailto:${SUPPORT_EMAIL}?subject=Sales` },
      { icon: "phone-portrait-outline", title: "Status & support", body: "Check service status if something isn't working as expected.", action: "View status", href: "/status" },
    ],
    bandH2: "Ready to try it yourself?",
    bandBody: "Get started free today — no commitment.",
    tryFree: "Try it free",
    seePricing: "See pricing",
  },
} as const;

export default function ContactContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const home = lang === "en" ? "/en" : "/";
  const pricingHref = lang === "en" ? "/en/pricing" : "/pricing";
  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}`}</style>

      <LandingNav lang={lang} />

      {/* ===== HERO ===== */}
      <section style={{ background: "var(--green-900)", color: "var(--text-on-hero)", paddingTop: 64 }}>
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "104px 32px 96px", textAlign: "center" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#F4C8A6" }}>{t.eyebrow}</div>
          <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 54, lineHeight: 1.06, letterSpacing: "-0.025em", margin: "18px 0 0", color: "#fff", textWrap: "balance" }}>{t.h1}</h1>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "34em", margin: "22px auto 0" }}>{t.sub}</p>
        </div>
      </section>

      {/* ===== CONTACT CARDS ===== */}
      <section style={{ padding: "96px 0" }}>
        <div className="ds-3col" style={{ maxWidth: 1180, margin: "0 auto", padding: "0 32px", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
          {t.cards.map((card) => {
            const isExternal = card.href.startsWith("mailto:");
            const inner = (
              <>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: "var(--green-100)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={card.icon} size={24} color="var(--brand)" />
                </div>
                <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 21, letterSpacing: "-0.01em", margin: 0, color: "var(--green-900)" }}>{card.title}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 }}>{card.body}</p>
                <span style={{ marginTop: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "var(--accent-strong)" }}>
                  {card.action}<Icon name="arrow-forward" size={15} color="var(--accent-strong)" />
                </span>
              </>
            );
            const cardStyle: React.CSSProperties = {
              background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18,
              padding: 28, boxShadow: "0 6px 16px rgba(16,33,27,0.06)", display: "flex",
              flexDirection: "column", gap: 15, minHeight: 220, textDecoration: "none", color: "inherit",
            };
            return isExternal ? (
              <a key={card.title} href={card.href} className="card-link" style={cardStyle}>{inner}</a>
            ) : (
              <Link key={card.title} href={card.href} className="card-link" style={cardStyle}>{inner}</Link>
            );
          })}
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section style={{ padding: "0 0 96px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ background: "var(--green-900)", borderRadius: 26, padding: "64px 48px", textAlign: "center" }}>
            <img src="/svendetid-logo.png" alt="" style={{ width: 64, height: 64, margin: "0 auto 18px", display: "block", opacity: 0.96 }} />
            <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, letterSpacing: "-0.02em", lineHeight: 1.08, margin: "0 auto", maxWidth: "18em", color: "#fff" }}>{t.bandH2}</h2>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "30em", margin: "16px auto 0" }}>{t.bandBody}</p>
            <div className="ds-cta-row" style={{ display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center", marginTop: 32 }}>
              <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.tryFree}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
              <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/contact" enHref="/en/contact" />
    </div>
  );
}
